/**
 * Streaming scroll policy, shared by normal chat and Work on every platform.
 *
 * Following the live tail is opt-in: the reader arms it with the
 * down-arrow / jump-to-live control and any upward scroll releases it. Nothing
 * else — sending, opening a thread, a reply finishing — glues the viewport to
 * the bottom.
 *
 * Without follow-live normal chat holds its scroll position as the reply grows.
 * Work instead "reveals" the turn: the prompt the reader just
 * sent is aligned to the top of the viewport and the reply grows downward
 * beneath it. Alignment is idempotent (scrollTop is clamped, so it can only
 * move while the list is still too short to put the prompt at the top) and
 * it is abandoned the moment the reader scrolls, so the view never chases the
 * tail of a long answer and never jumps once the stream ends.
 */

export const WORK_STREAM_SCROLL = {
  TAIL: 'tail',
  START: 'start',
  HOLD: 'hold',
};

/**
 * The reply that is currently being written, if any. Empty drafts are
 * ignored: a placeholder row with no text is not yet something to align to.
 */
export function findStreamingReply(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role !== 'assistant') continue;
    if (!(message.isStreaming || message.isTyping || message.isThinking)) continue;
    const content = String(message.content || '');
    const thinking = String(message.thinkingContent || '');
    if (!content.trim() && !thinking.trim()) continue;
    return { id: message.id || message._id || null, length: content.length + thinking.length };
  }
  return null;
}

/**
 * TAIL keeps the viewport on the bottom of the list (explicit follow-live, or a
 * positioning pin before any reply text exists). HOLD leaves normal chat at
 * the reader's scroll position. START lets Work align the current turn without
 * following the tail of a long reply.
 */
export function getStreamScrollMode({ hasStreamingText, followLive, isCoworkerChat = false } = {}) {
  if (hasStreamingText && !followLive) {
    return isCoworkerChat ? WORK_STREAM_SCROLL.START : WORK_STREAM_SCROLL.HOLD;
  }
  return WORK_STREAM_SCROLL.TAIL;
}

/**
 * Whether a content-size change should re-align the current turn's prompt to
 * the top of the viewport. Only while the reader is still at the tail (so the
 * motion is bounded by one viewport and never crosses unread rows), never
 * while they follow the live tail explicitly, and never while another
 * positioning mechanism owns the viewport.
 */
export function shouldRevealTurn({
  isCoworkerChat = false,
  armed = false,
  followLive = false,
  atBottom = false,
  anchorPinned = false,
  bottomPinned = false,
  studioOpen = false,
} = {}) {
  // Even bounded prompt alignment drags chat readers through the first screen
  // of a response. Only Work uses progressive alignment.
  if (!isCoworkerChat) return false;
  if (!armed) return false;
  if (followLive) return false;
  if (anchorPinned) return false;
  // A send / open pin is still carrying the viewport to the bottom; it hands
  // over once the reply has text (see getStreamScrollMode).
  if (bottomPinned) return false;
  if (studioOpen) return false;
  return atBottom;
}

/**
 * Align `messageClientTop` with the scroller's top, leaving `padding` px.
 * Returns the next scrollTop (clamped at 0).
 */
export function nextScrollTopToAlignStart({
  scrollerScrollTop = 0,
  scrollerClientTop = 0,
  messageClientTop = 0,
  padding = 12,
} = {}) {
  return Math.max(0, scrollerScrollTop + (messageClientTop - scrollerClientTop) - padding);
}

/**
 * Studio open remounts / reflows the chat column as stills and clips land.
 * RN Web FlatList then snaps scrollTop to 0. Restore the last good offset
 * unless the user is navigating the thread — especially scrolling down from
 * the top. Restoring during that gesture pins them on older rows and the
 * list never grows far enough to reach the latest messages.
 */
export function shouldRestoreStudioScroll({
  previousOffset = 0,
  nextOffset = 0,
  userScrolling = false,
  userIntentUntil = 0,
  now = Date.now(),
  lastDownwardAt = 0,
  pinningToBottom = false,
} = {}) {
  if (userScrolling) return false;
  if (pinningToBottom) return false;
  if (now < Number(userIntentUntil || 0)) return false;
  if (now - Number(lastDownwardAt || 0) < 2500) return false;
  if (previousOffset < 80) return false;
  return previousOffset > 80 && nextOffset < 32;
}

/**
 * Jump-to-live / scroll-to-bottom is normally toggled from scroll events.
 * In-place streams grow content without moving scrollTop, so the button must
 * also be derived from content-size changes or it stays invisible.
 */
export function shouldShowScrollToBottomButton({
  contentHeight = 0,
  layoutHeight = 0,
  scrollTop = 0,
  bottomThreshold = 100,
} = {}) {
  const canScroll = contentHeight > layoutHeight + 20;
  if (!canScroll) return false;
  return scrollTop + layoutHeight < contentHeight - bottomThreshold;
}
